/** @file tileDrafts: unsaved pixels of every open Tile tab, its local undo / redo, and the open tile prompt. */
import type { Image } from '@zilo/pose3d-image-io/types/image';
import { createStore } from 'zustand/vanilla';

/** One undo step: the pixels and the version they had. */
export type TileSnapshot = { image: Image; version: number };

/**
 * A tab's canvas: `image` (never mutated once stored; edits store a copy), its `version` (dirty while it differs from
 * `savedVersion`), the local undo and redo stacks (100 steps), and `open` while a stroke has not pushed its step yet.
 */
export type TileDraft = { image: Image; version: number; savedVersion: number; undo: TileSnapshot[]; redo: TileSnapshot[]; open: boolean };

/** A question the Tile screen is asking: close with unsaved strokes, or how to fit an image of another size. */
export type TilePrompt = { kind: 'close'; key: string } | { kind: 'fit'; key: string; name: string; image: Image } | null;

/** The drafts by `tileDraftKey`, the prompt, and the actions over them. */
export type TileDraftsState = {
  drafts: Record<string, TileDraft>;
  prompt: TilePrompt;
  /** Creates the draft from `image` (saved, no history) unless one exists. */
  start: (key: string, image: Image) => void;
  /** Opens a stroke: the next edit that changes a pixel pushes one undo step; later edits join it. */
  stroke: (key: string) => void;
  /** Runs `edit` on a copy of the pixels; a change stores the copy under a new version. Returns whether it changed. */
  edit: (key: string, edit: (image: Image) => boolean) => boolean;
  undo: (key: string) => void;
  redo: (key: string) => void;
  /** Marks `version` as the one on the server (the dot clears unless strokes came after it). */
  saved: (key: string, version: number) => void;
  /** Drops the draft (Discard, or the tab closed). */
  discard: (key: string) => void;
  setPrompt: (prompt: TilePrompt) => void;
};

const STEPS = 100;
const copyOf = (image: Image): Image => ({ width: image.width, height: image.height, data: new Uint8ClampedArray(image.data) });

/** One undo (or redo) step: the newest snapshot of that stack becomes the pixels; the current ones go on the other. */
function travel(d: TileDraft, dir: 'undo' | 'redo'): Partial<TileDraft> | null {
  const from = dir === 'undo' ? d.undo : d.redo;
  const back = from.at(-1);
  if (back === undefined) return null;
  const rest = from.slice(0, -1);
  const other = [...(dir === 'undo' ? d.redo : d.undo), { image: d.image, version: d.version }];
  return { image: back.image, version: back.version, open: false, undo: dir === 'undo' ? rest : other, redo: dir === 'undo' ? other : rest };
}

/** The page's one draft store (drafts outlive the screen, so a tab keeps its strokes while another one shows). */
export const tileDrafts = createStore<TileDraftsState>()((set, get) => {
  let counter = 0;
  const update = (key: string, patch: (d: TileDraft) => Partial<TileDraft> | null): void => {
    const d = get().drafts[key];
    const p = d === undefined ? null : patch(d);
    if (d !== undefined && p !== null) set((s) => ({ drafts: { ...s.drafts, [key]: { ...d, ...p } } }));
  };
  return {
    drafts: {},
    prompt: null,
    start: (key, image) => {
      if (get().drafts[key] !== undefined) return;
      counter += 1;
      set((s) => ({ drafts: { ...s.drafts, [key]: { image, version: counter, savedVersion: counter, undo: [], redo: [], open: false } } }));
    },
    stroke: (key) => { update(key, () => ({ open: true })); },
    edit: (key, edit) => {
      const d = get().drafts[key];
      if (d === undefined) return false;
      const next = copyOf(d.image);
      if (!edit(next)) return false;
      counter += 1;
      const undo = d.open ? [...d.undo, { image: d.image, version: d.version }].slice(-STEPS) : d.undo;
      update(key, () => ({ image: next, version: counter, open: false, undo, redo: d.open ? [] : d.redo }));
      return true;
    },
    undo: (key) => { update(key, (d) => travel(d, 'undo')); },
    redo: (key) => { update(key, (d) => travel(d, 'redo')); },
    saved: (key, version) => { update(key, () => ({ savedVersion: version })); },
    discard: (key) => {
      set((s) => ({
        drafts: Object.fromEntries(Object.entries(s.drafts).filter(([k]) => k !== key)),
        prompt: s.prompt?.key === key ? null : s.prompt,
      }));
    },
    setPrompt: (prompt) => { set({ prompt }); },
  };
});
